import { useEffect, useState } from 'react';
import financasApi from '@/lib/financasApi';
import { Wallet, TrendingUp, TrendingDown, Target, ArrowUpRight, ArrowDownRight, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function Kpi({ label, value, icon: Icon, color = 'emerald', sublabel }) {
  const colors = {
    emerald: 'from-emerald-500/20 to-emerald-700/10 border-emerald-500/30 text-emerald-400',
    red: 'from-red-500/20 to-red-700/10 border-red-500/30 text-red-400',
    blue: 'from-blue-500/20 to-blue-700/10 border-blue-500/30 text-blue-400',
    amber: 'from-amber-500/20 to-amber-700/10 border-amber-500/30 text-amber-400',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-3 md:p-5`}>
      <div className="flex items-start justify-between mb-2 md:mb-3">
        <p className="text-[10px] md:text-xs uppercase tracking-wider text-slate-400">{label}</p>
        <Icon className={`w-4 h-4 md:w-5 md:h-5 ${colors[color].split(' ').pop()}`} />
      </div>
      <p className="text-lg md:text-2xl font-bold text-white truncate">{value}</p>
      {sublabel && <p className="text-[10px] md:text-xs text-slate-400 mt-1 hidden md:block">{sublabel}</p>}
    </div>
  );
}

export default function FinancasDashboard() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [meiData, setMeiData] = useState(null);

  useEffect(() => {
    financasApi.get(`/financas/dashboard?mes=${mes}&ano=${ano}`).then(({ data }) => setData(data)).catch(() => {});
    financasApi.get('/financas/mei/status').then(({ data }) => setMeiData(data)).catch(() => {});
  }, [mes, ano]);

  const navMonth = (delta) => {
    let m = mes + delta;
    let a = ano;
    if (m > 12) { m = 1; a += 1; }
    if (m < 1) { m = 12; a -= 1; }
    setMes(m); setAno(a);
  };

  const serieFormatada = (data?.serie_mensal || []).map(s => ({
    mes: s.mes.split('-')[1] + '/' + s.mes.split('-')[0].slice(2),
    Entradas: s.entradas,
    Saídas: s.saidas,
    Saldo: s.saldo,
  }));

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Dashboard Financeiro</h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Visão geral das suas finanças</p>
        </div>
        <div className="flex items-center gap-2 bg-slate-900 border border-white/10 rounded-lg px-3 py-2 self-start sm:self-auto">
          <button onClick={() => navMonth(-1)} className="text-slate-400 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-white font-medium text-sm px-3" data-testid="financas-dashboard-periodo">{meses[mes - 1]}/{ano}</span>
          <button onClick={() => navMonth(1)} className="text-slate-400 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Kpi label="Saldo Atual" value={fmtBRL(data?.saldo_atual)} icon={Wallet} color="emerald" sublabel="Total acumulado" />
        <Kpi label="Entradas (mês)" value={fmtBRL(data?.entradas_mes)} icon={ArrowUpRight} color="blue" />
        <Kpi label="Saídas (mês)" value={fmtBRL(data?.saidas_mes)} icon={ArrowDownRight} color="red" />
        <Kpi label="Lucro do Mês" value={fmtBRL(data?.lucro_mes)} icon={TrendingUp} color={data?.lucro_mes >= 0 ? 'emerald' : 'red'} />
      </div>

      {/* MEI alert + Contas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {meiData && (
          <div className={`lg:col-span-2 rounded-xl p-5 border ${meiData.alerta ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-900 border-white/10'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Target className="w-4 h-4" /> Limite MEI/ME ({meiData.ano})
                </p>
                <p className="text-xl font-bold text-white mt-1">{fmtBRL(meiData.faturamento_ano)} / {fmtBRL(meiData.limite_anual)}</p>
              </div>
              {meiData.alerta && (
                <div className="flex items-center gap-1 bg-amber-500/20 px-3 py-1 rounded-full">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-300 text-xs font-medium">{meiData.ultrapassou ? 'LIMITE EXCEDIDO' : 'ATENÇÃO'}</span>
                </div>
              )}
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${meiData.ultrapassou ? 'bg-red-500' : meiData.alerta ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, meiData.percentual_uso)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-2">
              <span>{meiData.percentual_uso.toFixed(1)}% utilizado</span>
              <span>Estimativa anual: {fmtBRL(meiData.estimativa_anual)}</span>
            </div>
          </div>
        )}
        <div className="rounded-xl p-5 bg-slate-900 border border-white/10">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Pendências</p>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-300 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> A receber</span>
              <span className="text-emerald-400 font-semibold">{fmtBRL(data?.contas_receber)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-300 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-400" /> A pagar</span>
              <span className="text-red-400 font-semibold">{fmtBRL(data?.contas_pagar)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Evolução (últimos 6 meses)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={serieFormatada}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="mes" stroke="#64748b" />
              <YAxis stroke="#64748b" tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                formatter={(v) => fmtBRL(v)}
              />
              <Legend />
              <Line type="monotone" dataKey="Entradas" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Saídas" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Saldo" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Top Saídas (mês)</h3>
          {data?.top_categorias?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.top_categorias} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" stroke="#64748b" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="categoria" stroke="#64748b" width={100} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} formatter={(v) => fmtBRL(v)} />
                <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-500 text-sm">Sem saídas no mês</div>
          )}
        </div>
      </div>
    </div>
  );
}
